import React, { useEffect, useState } from 'react'
import { CircularProgress, Grid, IconButton, Paper, TextField, Tooltip } from '@mui/material'
import DeviceDispatchListTable from '~/components/DeviceDispatchListTable'
import EditIcon from '@mui/icons-material/Edit'
import useDispatchVehicleState from '../../stores/DispatchVehicleState'
import { useLang } from '~/hooks/useLang'
import useDispatchState from '../../stores/DispatchState'
import NameField from '~/features/Entry/components/NameField'
import PhoneTextField from '~/components/PhoneField'
import { truncateText } from '~/utils/strings'
import { formatPhone } from '~/utils/format'
import useSystemState from '~/features/System/stores/SystemState'
import { useTheme } from '~/hooks/useTheme'
import { Downgraded, useHookstate } from '@hookstate/core'

export interface RowsProps {
  readonly id: number
  readonly device_id: number
  readonly identification: string
  readonly status_id: number
  readonly status_name: string
  readonly status: string
  readonly status_changed_at: string
}


// eslint-disable-next-line max-lines-per-function
export default function EventInputRequesterDispatch({
  dispatchId,
  isValid,
  isValidEdit,
  canEdit = true,
  readonly = false,
  dispatchViewMode = false,
}) {
  const { translate } = useLang()
  const {
    vehicleListInDispatch,
    vehicleListInDispatchViewModeState,
    syncVehicleListInDispatch,
    setDefaultvehicleListInDispatch,
  } = useDispatchVehicleState()
  const dispatchState = useDispatchState()
  const { occurrence, id: dispatchIdState } = dispatchViewMode ? dispatchState.dispatchHistoric() : dispatchState.dispatch()
  const { dispatchStatusActions } = useDispatchState()
  const { systemConfig } = useSystemState()
  const { darkMode } = useTheme()
  const loading = useHookstate(true)
  const listVehiclesInDispatch = useHookstate(dispatchViewMode ? vehicleListInDispatchViewModeState().attach(Downgraded).value : vehicleListInDispatch().attach(Downgraded).value)
  const [syncSuccess, setSyncSuccess] = useState(true)
  const [syncError, setSyncError] = useState<string | null>(null) // eslint-disable-line @typescript-eslint/no-unused-vars
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isMounted = true
    listVehiclesInDispatch.set([])

    const getVehicleListInDispatch = async () => {
      if (!dispatchId) {
        if (isMounted) {
          setSyncSuccess(true)
          setSyncError(null)
        }
        return
      }

      try {
        const success = await syncVehicleListInDispatch(dispatchId, dispatchViewMode)

        if (!isMounted) return

        if (success) {
          const currentList = listVehiclesInDispatch.get()
          if (
            currentList.length > 0 &&
            Number(dispatchIdState.get()) !== Number(currentList[0].dispatch_id) &&
            !dispatchViewMode
          ) {
            setSyncSuccess(false)
            setSyncError('Erro ao sincronizar veículos com o despacho.')
          } else {
            setSyncSuccess(true)
            setSyncError(null)
          }
        } else {
          setSyncSuccess(false)
          setSyncError('Erro ao carregar lista de veículos.')
        }
      } catch (error) {
        if (isMounted) {
          setSyncSuccess(false)
          setSyncError(error instanceof Error ? error.message : 'Erro desconhecido')
        }
      }
    }

    getVehicleListInDispatch()
    loading.set(false)

    return () => {
      isMounted = false
      setDefaultvehicleListInDispatch(dispatchViewMode)
    }
  }, [dispatchId])


  return (
    <Grid
      item
      md={3.5}
      width={'100%'}
      display={'flex'}
      direction={'column'}
      alignItems={'center'}
      justifyContent={'flex-end'}
      gap={1}
      paddingLeft={1}
    >
      {loading.get() ? (
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress disableShrink />
        </Paper>
      ) : (
        <DeviceDispatchListTable
          readonly={readonly}
          loading={loading.get()}
          dispatchViewMode={dispatchViewMode}
          successSync={syncSuccess}
          refreshAction={async () => {
            if (!dispatchId) return

            try {
              const success = await syncVehicleListInDispatch(dispatchId, dispatchViewMode)

              if (success) {
                const currentList = listVehiclesInDispatch.get()
                if (
                  currentList.length > 0 &&
                  Number(dispatchIdState.get()) !== Number(currentList[0].dispatch_id) &&
                  !dispatchViewMode
                ) {
                  setSyncSuccess(false)
                  setSyncError('Erro ao sincronizar veículos com o despacho.')
                } else {
                  setSyncSuccess(true)
                  setSyncError(null)
                }
              } else {
                setSyncSuccess(false)
                setSyncError('Erro ao carregar lista de veículos.')
              }
            } catch (error) {
              setSyncSuccess(false)
              setSyncError(error instanceof Error ? error.message : 'Erro desconhecido')
            }
          }}
        />
      )
      }
      {systemConfig().displayRequesterDataInPrint.get() === '1' ? (
        <Grid container direction="column" spacing={1} height={'100%'} >
          {!dispatchStatusActions().editingDispatchRequester.get() || dispatchViewMode ? (
            <Grid item width={'100%'}>
              <Tooltip
                arrow
                placement="top-start"
                title={translate('Attendant, Requester, Phone:', {
                  attendant: occurrence.get().createdUser
                    ? `${occurrence.get()?.createdUser?.registration ? `[${occurrence.get()?.createdUser?.registration}]` : ''} ${occurrence.get()?.createdUser?.representationName}`
                    : translate('Not informed'),
                  requester: occurrence.name?.get()
                    ? occurrence.name.get()
                    : translate('Not informed'),
                  phone: occurrence.get()?.phone
                    ? `${formatPhone(occurrence.get().phone)}`
                    : translate('Not informed'),
                })}
                componentsProps={{
                  tooltip: {
                    sx: { whiteSpace: 'pre-line' },
                  },
                }}
              >
                <TextField
                  disabled={true}
                  placeholder={translate('Address')}
                  value={`${translate('Attendant')}: ${occurrence.get().createdUser
                    ? `${occurrence.get()?.createdUser?.registration
                      ? `[${occurrence.get()?.createdUser?.registration}]`
                      : ''} ${truncateText(String(occurrence.get()?.createdUser?.representationName),11)}`
                    : translate('Not informed')
                    }\n\n${translate('Requester')}: ${occurrence.name?.get()
                      ? truncateText(String(occurrence.name.get()), 11)
                      : translate('Not informed')
                    }\n\n${translate('Phone')}: ${occurrence.get()?.phone
                      ? `${formatPhone(occurrence.get().phone)}`
                      : translate('Not informed')
                    }`}
                  size="small"
                  fullWidth
                  multiline
                  rows={6}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'inherit',
                      color: 'inherit',
                      opacity: 1,
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled': {
                      '& fieldset': {
                        borderColor: darkMode
                          ? 'rgba(255, 255, 255, 0.09)'
                          : 'rgba(0, 0, 0, 0.15)',
                      },
                    },
                  }}
                  InputProps={{
                    style: {
                      paddingTop: '18px',
                      lineHeight: '1.3',
                    },
                    endAdornment:
                      canEdit && !readonly && !dispatchViewMode ? (
                        <IconButton
                          title={translate('Edit')}
                          disabled={!isValid || !isValidEdit}
                          onClick={() =>
                            dispatchStatusActions().editingDispatchRequester.set(
                              true
                            )
                          }
                          sx={{
                            position: 'absolute',
                            bottom: 20,
                            left: 238,
                            padding: '3px',
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      ) : (
                        false
                      ),
                  }}
                />
              </Tooltip>
            </Grid>
          ) : null}

          {dispatchStatusActions().editingDispatchRequester.get() && !dispatchViewMode ? (
            <Grid item width={'100%'}>
              <Grid item>
                <NameField isValid={isValid} name={occurrence.name} />
              </Grid>
              <Grid item marginTop={2.1}>
                <PhoneTextField
                  statePhone={occurrence.phone}
                  disabled={!isValid}
                  onClickButton={() => ''}
                  disabledLastCalls={true}
                />
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      ) : null}
    </Grid>
  )
}
